/**
 * HealthPage.tsx — the System Health dashboard (story S9).
 *
 * Fetches GET /api/health/overview on mount, polls every ~20s, and exposes a
 * "Refresh now" button for an immediate refetch. It renders the unified
 * snapshot as three card groups (Containers / Pipeline Flow / Dependencies),
 * each composed from the shared primitives (StatusBadge / MetricTile /
 * FreshnessIndicator).
 *
 * Failure is a first-class state (design §"Error Handling"):
 *   - 401/403  → the clean UnauthorizedScreen (never a broken page).
 *   - BFF unreachable → keep the LAST-KNOWN snapshot on screen + a top banner;
 *     degraded, never blank.
 *   - A red/grey board (collectors can't yet reach infra) is a VALID render,
 *     not an app error.
 *
 * The API client is injected (App wires the real, token-bearing client) so the
 * dashboard is testable against MSW with no MSAL coupling.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import {
  SOURCE_KEYS,
  type HealthItem,
  type HealthOverview,
  type SourceSnapshot,
  type SourceStatus,
} from '../../api/types';
import { StatusBadge } from '../../components/StatusBadge';
import { Skeleton } from '../../components/Skeleton';
import { MetricTile } from '../../components/MetricTile';
import { FreshnessIndicator } from '../../components/FreshnessIndicator';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { isAttentionStatus, sortBySeverity } from './severityOrder';
import floating from '../../lib/floating';

/** Default UI poll cadence (~20s, design §Polling). Overridable for tests. */
const DEFAULT_POLL_INTERVAL_MS = 20_000;

// --- Severity-led emphasis (design rubric §Elevation) ----------------------
// Emphasis is carried by STATUS TINT + RING only — never drop shadows, never a
// >1px accent stripe. Broken sources get a louder status ring + faint tint so
// red is the loudest thing on the board; calm ok/unknown cards stay elev-1.

/** elev-1 source card, weighted by status. Non-focal cards use this. */
const CARD_EMPHASIS: Record<SourceStatus, string> = {
  down: 'bg-surface-2 ring-1 ring-inset ring-status-down/50',
  degraded: 'bg-surface-2 ring-1 ring-inset ring-status-degraded/40',
  unknown: 'bg-surface-2 ring-1 ring-inset ring-ink-3/10',
  ok: 'bg-surface-2 ring-1 ring-inset ring-ink-3/10',
};

/**
 * elev-2 focal — the single worst/stalest source on the board. A heavier 2px
 * status ring + status tint makes it unmissable. At most ONE focal per screen,
 * and only for an attention status (down/degraded); a green/grey board gets no
 * alarm focal, so this map is intentionally partial.
 */
const FOCAL_EMPHASIS: Partial<Record<SourceStatus, string>> = {
  down: 'bg-status-down/10 ring-2 ring-inset ring-status-down',
  degraded: 'bg-status-degraded/10 ring-2 ring-inset ring-status-degraded',
};

/** Item-row tint, weighted by status — broken rows read louder than calm ones. */
const ITEM_EMPHASIS: Record<SourceStatus, string> = {
  down: 'bg-status-down/5 ring-1 ring-inset ring-status-down/30',
  degraded: 'bg-status-degraded/5 ring-1 ring-inset ring-status-degraded/25',
  unknown: 'bg-surface-1',
  ok: 'bg-surface-1',
};

export interface HealthPageProps {
  apiClient: ApiClient;
  /** Poll cadence in ms. Defaults to ~20s; injected small in tests. */
  pollIntervalMs?: number;
}

type LoadState = 'loading' | 'ready' | 'unauthorized';

const ItemRow = ({ item }: { item: HealthItem }) => (
  <li
    data-testid="health-item"
    data-item-key={item.key}
    data-status={item.status}
    className={`flex flex-col gap-2 rounded-lg p-3 ${ITEM_EMPHASIS[item.status]}`}
  >
    <div className="flex items-center justify-between gap-3">
      <span className="text-body font-medium text-ink-1">{item.label}</span>
      <StatusBadge status={item.status} />
    </div>
    {item.reason ? <p className="text-meta text-ink-3">{item.reason}</p> : null}
    {item.metrics.length > 0 ? (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {item.metrics.map((metric) => (
          <MetricTile key={`${item.key}-${metric.label}`} metric={metric} />
        ))}
      </div>
    ) : null}
  </li>
);

const SourceCard = ({
  source,
  label,
  isFocal,
}: {
  source: SourceSnapshot;
  label: string;
  /** True for the single worst/stalest source — promotes it to the elev-2 focal. */
  isFocal: boolean;
}) => {
  const { t } = useTranslation();
  const headingId = `health-source-${source.source}`;
  const emphasis = isFocal
    ? (FOCAL_EMPHASIS[source.status] ?? CARD_EMPHASIS[source.status])
    : CARD_EMPHASIS[source.status];
  // Broken-first within the group; source order is the stable tiebreaker.
  const sortedItems = sortBySeverity(source.items, (item) => item.status);
  const eyebrowColor = source.status === 'degraded' ? 'text-status-degraded' : 'text-status-down';
  const renderBranch1 = () => {
    if (sortedItems.length > 0)
      return (
        <ul className="flex flex-col gap-2">
          {sortedItems.map((item) => (
            <ItemRow key={item.key} item={item} />
          ))}
        </ul>
      );
    if (source.reason) return null;
    return <p className="text-meta text-ink-3">{t('health.noItems')}</p>;
  };

  return (
    <section
      data-testid="source-card"
      data-source={source.source}
      data-focal={isFocal ? 'true' : undefined}
      aria-labelledby={headingId}
      className={`flex flex-col gap-3 rounded-xl p-4 ${emphasis}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          {isFocal ? (
            <span
              data-testid="source-focal-eyebrow"
              className={`text-meta font-medium uppercase tracking-wide ${eyebrowColor}`}
            >
              {t('health.needsAttention')}
            </span>
          ) : null}
          <h2 id={headingId} className="text-h2 text-ink-1">
            {label}
          </h2>
        </div>
        <StatusBadge status={source.status} />
      </header>
      {source.reason ? (
        <p data-testid="source-reason" className="text-meta text-ink-3">
          {source.reason}
        </p>
      ) : null}
      {renderBranch1()}
    </section>
  );
};
const RefreshButton = ({
  onClick,
  refreshing,
  label,
  busyLabel,
}: {
  onClick: () => void;
  refreshing: boolean;
  label: string;
  busyLabel: string;
}) => (
  <button
    type="button"
    data-testid="health-refresh"
    onClick={onClick}
    disabled={refreshing}
    aria-busy={refreshing}
    className="inline-flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1 disabled:opacity-60"
  >
    {refreshing ? busyLabel : label}
  </button>
);

export const HealthPage = ({
  apiClient,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: HealthPageProps) => {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<HealthOverview | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [unreachable, setUnreachable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Track mount so an in-flight fetch resolving after unmount is a no-op.
  const mountedRef = useRef(true);
  // Monotonic request id: only the LATEST fetch may commit state, so an older
  // slow poll can never clobber a newer response (race-safe without relying on
  // AbortController, which is cross-realm-incompatible under jsdom+undici).
  const requestSeq = useRef(0);
  // Holds the live poll handle so we can stop polling outside the effect (e.g.
  // once the session is unauthorized) without tearing the component down.
  const pollIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIdRef.current !== null) {
      clearInterval(pollIdRef.current);
      pollIdRef.current = null;
    }
  }, []);

  const fetchOverview = useCallback(
    async ({ userInitiated = false }: { userInitiated?: boolean } = {}) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const isCurrent = () => mountedRef.current && seq === requestSeq.current;
      // Only a user-initiated refresh toggles the button's busy state; silent
      // background polls must not disable "Refresh now" every ~20s.
      if (userInitiated) setRefreshing(true);
      try {
        const data = await apiClient.getHealthOverview();
        if (!isCurrent()) return;
        setSnapshot(data);
        setUnreachable(false);
        setState('ready');
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setState('unauthorized');
          // A 401/403 won't self-heal on the next tick — stop hammering the BFF.
          stopPolling();
          return;
        }
        // Network / 5xx: keep the last-known snapshot, raise the banner.
        // Never blank the board on a transient BFF failure.
        setUnreachable(true);
        setState((prev) => (prev === 'loading' ? 'loading' : 'ready'));
      } finally {
        if (userInitiated && isCurrent()) setRefreshing(false);
      }
    },
    [apiClient, stopPolling],
  );

  // Mount fetch + ~20s poll.
  useEffect(() => {
    mountedRef.current = true;
    floating(fetchOverview());
    pollIdRef.current = setInterval(() => {
      floating(fetchOverview());
    }, pollIntervalMs);

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [fetchOverview, pollIntervalMs, stopPolling]);

  const handleRefresh = useCallback(() => {
    floating(fetchOverview({ userInitiated: true }));
  }, [fetchOverview]);

  // Global "refresh" hotkey (see AppShell) forces an immediate refetch.
  useEffect(() => {
    const onRefresh = () => floating(fetchOverview({ userInitiated: true }));
    window.addEventListener('tcn:refresh', onRefresh);
    return () => window.removeEventListener('tcn:refresh', onRefresh);
  }, [fetchOverview]);

  if (state === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  // Initial load with no snapshot yet.
  if (state === 'loading' && !snapshot) {
    if (unreachable) {
      return (
        <section aria-labelledby="health-heading" className="flex flex-col gap-4">
          <h1 id="health-heading" className="text-h1 text-ink-1">
            {t('health.title')}
          </h1>
          <p
            role="alert"
            data-testid="health-unreachable-nodata"
            className="text-body text-status-down"
          >
            {t('health.unreachableNoData')}
          </p>
          <div>
            <RefreshButton
              onClick={handleRefresh}
              refreshing={refreshing}
              label={t('health.refresh')}
              busyLabel={t('health.refreshing')}
            />
          </div>
        </section>
      );
    }
    return (
      <section aria-labelledby="health-heading" className="flex flex-col gap-3">
        <h1 id="health-heading" className="text-h1 text-ink-1">
          {t('health.title')}
        </h1>
        <Skeleton data-testid="health-loading" label={t('health.loading')} className="h-40" />
      </section>
    );
  }

  // Map fixed SOURCE_KEYS → snapshot (canonical order), then re-order worst-first
  // by severity so the broken group floats to the top-left of the grid where the
  // eye lands. Canonical source order is the stable tiebreaker within a band.
  const sourcesByKey = new Map((snapshot?.sources ?? []).map((s) => [s.source, s]));
  const presentSources = SOURCE_KEYS.map((key) => sourcesByKey.get(key)).filter(
    (s): s is SourceSnapshot => s !== undefined,
  );
  const orderedSources = sortBySeverity(presentSources, (s) => s.status);
  // The elev-2 focal is the single worst source — first after the severity sort,
  // and only when it is actually broken (down/degraded). A green/grey board earns
  // no alarm focal. At most one focal per screen (rubric §Elevation).
  const focalSourceKey =
    orderedSources.length > 0 && isAttentionStatus(orderedSources[0]!.status)
      ? orderedSources[0]!.source
      : null;

  return (
    <section aria-labelledby="health-heading" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="health-heading" className="text-h1 text-ink-1">
          {t('health.title')}
        </h1>
        <div className="flex items-center gap-4">
          {snapshot ? (
            <FreshnessIndicator
              timestamp={snapshot.generated_at}
              cacheAgeSeconds={snapshot.cache_age_seconds}
            />
          ) : null}
          <RefreshButton
            onClick={handleRefresh}
            refreshing={refreshing}
            label={t('health.refresh')}
            busyLabel={t('health.refreshing')}
          />
        </div>
      </header>

      {snapshot ? (
        <div
          className={`flex items-center justify-between gap-3 rounded-xl p-4 ${CARD_EMPHASIS[snapshot.overall]}`}
          data-testid="health-overall"
        >
          <span className="text-h2 text-ink-1">{t('health.overall')}</span>
          <StatusBadge status={snapshot.overall} />
        </div>
      ) : null}

      {unreachable ? (
        <p
          role="status"
          data-testid="health-unreachable-banner"
          className="rounded-lg bg-status-degraded/15 px-4 py-2 text-meta text-status-degraded ring-1 ring-inset ring-status-degraded/40"
        >
          {t('health.unreachableBanner')}
        </p>
      ) : null}

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        {orderedSources.map((source) => (
          <SourceCard
            key={source.source}
            source={source}
            label={t(`health.sources.${source.source}`)}
            isFocal={source.source === focalSourceKey}
          />
        ))}
      </div>
    </section>
  );
};
